import { useId, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, Search } from 'lucide-react';
import { Abas, PainelAba } from '../../componentes/abas';
import { Botao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { AreaSensivel, Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { Menu, type ItemMenu } from '../../componentes/menu';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { del, getTodos, mensagemDeErro, patch, post } from '../../nucleo/api';
import { nomePessoa, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { useNomesDoChat, usePessoasDoChat } from './dados';
import { AvatarCanal } from './ListaCanais';
import { iniciais, nomeDoCanalNaTela, type Canal, type CanalDetalhe, type Pessoa, type Visibilidade } from './tipos';

const CLASSE_AREA = 'w-full min-w-0 resize-y rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco';

/** Lista de pessoas com busca e caixas de seleção (grupo, adicionar participantes). */
function EscolherPessoas({ pessoas, escolhidas, aoMudar }: { pessoas: Pessoa[]; escolhidas: Set<string>; aoMudar: (s: Set<string>) => void }) {
  const { t } = useTranslation('equipe');
  const [busca, setBusca] = useState('');
  const visiveis = pessoas.filter((p) => p.nome.toLocaleLowerCase().includes(busca.trim().toLocaleLowerCase()));
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
        <Entrada type="search" aria-label={t('nova.buscarPessoa')} placeholder={t('nova.buscarPessoa')} className="pl-9" value={busca} onChange={(e) => setBusca(e.target.value)} />
      </div>
      {!pessoas.length ? (
        <p className="text-sm text-tinta-3">{t('nova.semPessoas')}</p>
      ) : !visiveis.length ? (
        <p className="text-sm text-tinta-3">{t('nova.nenhumaPessoa')}</p>
      ) : (
        <ul className="max-h-56 divide-y divide-borda overflow-y-auto rounded-lg border border-borda">
          {visiveis.map((p) => (
            <li key={p.id}>
              <label className="flex min-w-0 cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-sup-2">
                <input
                  type="checkbox"
                  className="size-4 shrink-0 accent-acento-forte"
                  checked={escolhidas.has(p.id)}
                  onChange={(e) => {
                    const n = new Set(escolhidas);
                    if (e.target.checked) n.add(p.id);
                    else n.delete(p.id);
                    aoMudar(n);
                  }}
                />
                <span aria-hidden className="flex size-7 shrink-0 items-center justify-center rounded-full border border-borda bg-sup-3 text-xs font-semibold text-tinta-2">
                  {iniciais(p.nome)}
                </span>
                <TextoCortado className="text-tinta-1">{nomePessoa(p.nome)}</TextoCortado>
              </label>
            </li>
          ))}
        </ul>
      )}
      {escolhidas.size > 0 && <p className="text-xs text-tinta-3">{t('nova.selecionadas', { count: escolhidas.size })}</p>}
    </div>
  );
}

type TipoNova = 'direta' | 'grupo' | 'departamento';

/**
 * Nova conversa: direta (escolhe a pessoa e abre), grupo (nome, quem vê e participantes) ou canal de departamento (só quem
 * administra o chat; com o Pessoas, o departamento traz o nome e os funcionários).
 */
export function NovaConversa({ aberto, aoFechar, aoAbrir }: { aberto: boolean; aoFechar: () => void; aoAbrir: (id: string) => void }) {
  const { t } = useTranslation('equipe');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const idAbas = useId();
  const idNome = useId();
  const idDescricao = useId();
  const idVis = useId();
  const idDep = useId();
  const admin = s.pode('equipe.administrar');
  const [tipo, setTipo] = useState<TipoNova>('direta');
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [visibilidade, setVisibilidade] = useState<Visibilidade>('privado');
  const [departamento, setDepartamento] = useState('');
  const [escolhidas, setEscolhidas] = useState<Set<string>>(new Set());
  const [buscaDireta, setBuscaDireta] = useState('');
  const pessoas = usePessoasDoChat(aberto);
  const outras = (pessoas.data?.itens ?? []).filter((p) => p.id !== s.usuario.id);
  const comPessoas = s.instalado('pessoas') && s.pode('pessoas.ver');
  const deps = useQuery({ queryKey: ['equipe', 'departamentos'], queryFn: () => getTodos<{ id: string; nome: string }>('/pessoas/departamentos'), enabled: aberto && tipo === 'departamento' && comPessoas, staleTime: 60_000 });

  const limpar = () => {
    setNome('');
    setDescricao('');
    setVisibilidade('privado');
    setDepartamento('');
    setEscolhidas(new Set());
    setBuscaDireta('');
  };
  const fechar = () => {
    limpar();
    aoFechar();
  };
  const direta = useMutation({
    mutationFn: (usuario_id: string) => post<{ id: string }>('/equipe/diretas', { usuario_id }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['equipe'] });
      fechar();
      aoAbrir(r.id);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const criar = useMutation({
    mutationFn: () =>
      post<{ id: string; recusados: string[] }>('/equipe/canais', {
        tipo: tipo === 'departamento' ? 'departamento' : 'grupo',
        nome,
        descricao: descricao.trim() || null,
        visibilidade,
        departamento_id: tipo === 'departamento' && departamento ? departamento : null,
        participantes: [...escolhidas],
      }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['equipe'] });
      if (r.recusados?.length) {
        const nomes = r.recusados.map((id) => nomePessoa(outras.find((p) => p.id === id)?.nome ?? id.slice(0, 8))).join(', ');
        toast.info(t('nova.recusados', { nomes }));
      }
      fechar();
      aoAbrir(r.id);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const itens = (['direta', 'grupo', ...(admin ? ['departamento'] : [])] as TipoNova[]).map((x) => ({ chave: x, rotulo: t(x === 'direta' ? 'tipos.direta' : x === 'grupo' ? 'tipos.grupo' : 'tipos.departamento') }));
  const visiveisDireta = outras.filter((p) => p.nome.toLocaleLowerCase().includes(buscaDireta.trim().toLocaleLowerCase()));

  return (
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      titulo={t('nova.titulo')}
      rodape={
        tipo === 'direta' ? undefined : (
          <>
            <Botao variante="fantasma" onClick={fechar}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" disabled={!nome.trim()} carregando={criar.isPending} onClick={() => criar.mutate()}>
              {t('acoes.criar')}
            </Botao>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <Abas variante="segmentado" idBase={idAbas} rotulo={t('nova.tipo')} ativa={tipo} aoMudar={setTipo} itens={itens} />
        <PainelAba idBase={idAbas} chave={tipo}>
          {tipo === 'direta' ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-tinta-3">{t('nova.diretaTexto')}</p>
              <div className="relative">
                <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
                <Entrada type="search" aria-label={t('nova.buscarPessoa')} placeholder={t('nova.buscarPessoa')} className="pl-9" value={buscaDireta} onChange={(e) => setBuscaDireta(e.target.value)} />
              </div>
              {pessoas.isPending ? (
                <Carregando />
              ) : pessoas.isError ? (
                <Erro erro={pessoas.error} aoTentar={() => pessoas.refetch()} />
              ) : !outras.length ? (
                <p className="text-sm text-tinta-3">{t('nova.semPessoas')}</p>
              ) : !visiveisDireta.length ? (
                <p className="text-sm text-tinta-3">{t('nova.nenhumaPessoa')}</p>
              ) : (
                <ul className="max-h-72 divide-y divide-borda overflow-y-auto rounded-lg border border-borda">
                  {visiveisDireta.map((p) => (
                    <li key={p.id}>
                      <button type="button" data-pessoa={p.id} disabled={direta.isPending} onClick={() => direta.mutate(p.id)} className="flex w-full min-w-0 items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-sup-2">
                        <span aria-hidden className="flex size-8 shrink-0 items-center justify-center rounded-full border border-borda bg-sup-3 text-xs font-semibold text-tinta-2">
                          {iniciais(p.nome)}
                        </span>
                        <TextoCortado className="font-medium text-tinta-1">{nomePessoa(p.nome)}</TextoCortado>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-tinta-3">{tipo === 'grupo' ? t('nova.grupoTexto') : t('nova.departamentoTexto')}</p>
              {tipo === 'departamento' && comPessoas && (
                <Rotulo id={idDep} rotulo={t('nova.departamento')}>
                  <Selecao
                    id={idDep}
                    vazio={t('nova.semDepartamento')}
                    opcoes={(deps.data?.itens ?? []).map((d) => ({ valor: d.id, rotulo: d.nome }))}
                    value={departamento}
                    onChange={(e) => {
                      setDepartamento(e.target.value);
                      const d = deps.data?.itens.find((x) => x.id === e.target.value);
                      if (d && !nome.trim()) setNome(d.nome);
                    }}
                  />
                </Rotulo>
              )}
              <Rotulo id={idNome} rotulo={t('nova.nome')} obrigatorio>
                <Entrada id={idNome} value={nome} maxLength={80} onChange={(e) => setNome(e.target.value)} />
              </Rotulo>
              <Rotulo id={idDescricao} rotulo={t('nova.descricao')}>
                <AreaSensivel id={idDescricao} rows={2} maxLength={500} value={descricao} onChange={(e) => setDescricao(e.target.value)} className={CLASSE_AREA} />
              </Rotulo>
              <Rotulo id={idVis} rotulo={t('nova.visibilidade')} ajuda={visibilidade === 'publico' ? t('visibilidade.publicoAjuda') : t('visibilidade.privadoAjuda')}>
                <Selecao
                  id={idVis}
                  opcoes={[
                    { valor: 'privado', rotulo: t('visibilidade.privado') },
                    { valor: 'publico', rotulo: t('visibilidade.publico') },
                  ]}
                  value={visibilidade}
                  onChange={(e) => setVisibilidade(e.target.value as Visibilidade)}
                />
              </Rotulo>
              <div className="flex flex-col gap-1.5">
                <p className="text-sm font-medium text-tinta-2">{t('nova.participantes')}</p>
                <EscolherPessoas pessoas={outras} escolhidas={escolhidas} aoMudar={setEscolhidas} />
              </div>
            </div>
          )}
        </PainelAba>
      </div>
    </Modal>
  );
}

/** Canais públicos do workspace: lê sem entrar (abre) ou entra para escrever. */
export function ExplorarCanais({ aberto, aoFechar, aoAbrir }: { aberto: boolean; aoFechar: () => void; aoAbrir: (id: string) => void }) {
  const { t } = useTranslation('equipe');
  const toast = useToast();
  const qc = useQueryClient();
  const s = useSessao();
  const q = useQuery({ queryKey: ['equipe', 'canais', 'publicos'], queryFn: () => getTodos<Canal>('/equipe/canais', { escopo: 'publicos' }), enabled: aberto });
  const nomes = useNomesDoChat();
  const entrar = useMutation({
    mutationFn: (id: string) => post(`/equipe/canais/${id}/entrar`).then(() => id),
    onSuccess: async (id) => {
      await qc.invalidateQueries({ queryKey: ['equipe'] });
      aoFechar();
      aoAbrir(id);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Modal aberto={aberto} aoFechar={aoFechar} titulo={t('explorar.titulo')}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-tinta-3">{t('explorar.texto')}</p>
        {q.isPending ? (
          <Carregando />
        ) : q.isError ? (
          <Erro erro={q.error} aoTentar={() => q.refetch()} />
        ) : !q.data.itens.length ? (
          <Vazio texto={t('explorar.vazio')} />
        ) : (
          <ul className="divide-y divide-borda rounded-lg border border-borda">
            {q.data.itens.map((c) => (
              <li key={c.id} className="flex min-w-0 flex-wrap items-center gap-3 px-3 py-2.5">
                <AvatarCanal canal={c} />
                <div className="min-w-0 flex-1 basis-40">
                  <TextoCortado como="p" className="text-sm font-medium text-tinta-1">
                    {nomeDoCanalNaTela(c, nomes)}
                  </TextoCortado>
                  <p className="text-xs text-tinta-3">
                    {t('lista.membros', { count: c.membros })}
                    {c.descricao ? ` · ${textoLivre(c.descricao, ...nomes)}` : ''}
                  </p>
                </div>
                {c.participa ? (
                  <>
                    <Selo cor="verde">{t('explorar.participa')}</Selo>
                    <Botao
                      tamanho="sm"
                      onClick={() => {
                        aoFechar();
                        aoAbrir(c.id);
                      }}
                    >
                      {t('acoes.abrir')}
                    </Botao>
                  </>
                ) : (
                  <>
                    <Botao
                      tamanho="sm"
                      variante="fantasma"
                      onClick={() => {
                        aoFechar();
                        aoAbrir(c.id);
                      }}
                    >
                      {t('acoes.abrir')}
                    </Botao>
                    {s.pode('equipe.enviar') && (
                      <Botao tamanho="sm" variante="primario" carregando={entrar.isPending && entrar.variables === c.id} onClick={() => entrar.mutate(c.id)}>
                        {t('acoes.entrar')}
                      </Botao>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

/** Participantes do canal: papel, adicionar pessoas, tornar dono ou membro e retirar (quem gere o canal). */
export function Membros({ canal, aberto, aoFechar }: { canal: CanalDetalhe; aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation('equipe');
  const eu = useSessao().usuario.id;
  const toast = useToast();
  const qc = useQueryClient();
  const pessoas = usePessoasDoChat(aberto && canal.pode_gerir);
  const [escolhidas, setEscolhidas] = useState<Set<string>>(new Set());
  const [retirar, setRetirar] = useState<{ id: string; nome: string } | null>(null);
  const fora = useMemo(() => (pessoas.data?.itens ?? []).filter((p) => !canal.participantes.some((x) => x.usuario_id === p.id)), [pessoas.data, canal.participantes]);
  const invalidar = () => qc.invalidateQueries({ queryKey: ['equipe'] });
  const adicionar = useMutation({
    mutationFn: () => post<{ adicionados: string[]; recusados: string[] }>(`/equipe/canais/${canal.id}/participantes`, { usuarios: [...escolhidas] }),
    onSuccess: async (r) => {
      setEscolhidas(new Set());
      if (r.recusados.length) toast.info(t('nova.recusados', { nomes: r.recusados.map((id) => nomePessoa(fora.find((p) => p.id === id)?.nome ?? id.slice(0, 8))).join(', ') }));
      await invalidar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const papel = useMutation({ mutationFn: (x: { id: string; papel: 'dono' | 'membro' }) => patch(`/equipe/canais/${canal.id}/participantes/${x.id}`, { papel: x.papel }), onSuccess: invalidar, onError: (e) => toast.erro(mensagemDeErro(e)) });
  const remover = useMutation({
    mutationFn: (id: string) => del(`/equipe/canais/${canal.id}/participantes/${id}`),
    onSuccess: async () => {
      setRetirar(null);
      await invalidar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const nomesChat = useNomesDoChat(canal.participantes.map((p) => p.nome));
  const nomeCanal = nomeDoCanalNaTela(canal, nomesChat);
  return (
    <Drawer aberto={aberto} aoFechar={aoFechar} titulo={t('membros.titulo', { canal: nomeCanal })}>
      <div className="flex flex-col gap-5">
        <ul className="divide-y divide-borda rounded-lg border border-borda" data-participantes>
          {canal.participantes.map((p) => {
            const acoes: ItemMenu[] = [];
            if (canal.pode_gerir) {
              acoes.push(p.papel === 'dono' ? { chave: 'membro', rotulo: t('membros.tornarMembro'), aoClicar: () => papel.mutate({ id: p.usuario_id, papel: 'membro' }) } : { chave: 'dono', rotulo: t('membros.tornarDono'), aoClicar: () => papel.mutate({ id: p.usuario_id, papel: 'dono' }) });
              if (p.usuario_id !== eu) acoes.push({ chave: 'retirar', rotulo: t('membros.remover'), aoClicar: () => setRetirar({ id: p.usuario_id, nome: p.nome }) });
            }
            return (
              <li key={p.usuario_id} className="flex min-w-0 items-center gap-3 px-3 py-2.5">
                <span aria-hidden className="flex size-8 shrink-0 items-center justify-center rounded-full border border-borda bg-sup-3 text-xs font-semibold text-tinta-2">
                  {iniciais(p.nome)}
                </span>
                <TextoCortado className="min-w-0 flex-1 text-sm text-tinta-1">{`${nomePessoa(p.nome)}${p.usuario_id === eu ? ` ${t('membros.voce')}` : ''}`}</TextoCortado>
                <Selo cor={p.papel === 'dono' ? 'acento' : 'neutro'}>{t(`papeis.${p.papel}`)}</Selo>
                {acoes.length > 0 && <Menu rotulo={t('membros.acoesDe', { nome: nomePessoa(p.nome) })} gatilho={<MoreHorizontal className="size-4" aria-hidden />} classeGatilho="rounded-md p-1.5 text-tinta-3 hover:bg-sup-3 hover:text-tinta-1" itens={acoes} classePainel="right-0 w-48" />}
              </li>
            );
          })}
        </ul>
        {canal.pode_gerir && !canal.arquivado && (
          <div className="flex flex-col gap-2">
            <h3 className="kicker">{t('membros.adicionar')}</h3>
            {pessoas.isPending ? (
              <Carregando />
            ) : !fora.length ? (
              <p className="text-sm text-tinta-3">{t('membros.nenhumParaAdicionar')}</p>
            ) : (
              <>
                <EscolherPessoas pessoas={fora} escolhidas={escolhidas} aoMudar={setEscolhidas} />
                <div className="flex justify-end">
                  <Botao variante="primario" tamanho="sm" disabled={!escolhidas.size} carregando={adicionar.isPending} onClick={() => adicionar.mutate()}>
                    {t('acoes.adicionar')}
                  </Botao>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      <Confirmar aberto={!!retirar} aoFechar={() => setRetirar(null)} aoConfirmar={() => retirar && remover.mutate(retirar.id)} titulo={t('membros.removerTitulo', { nome: nomePessoa(retirar?.nome) })} texto={t('membros.removerTexto')} perigoso carregando={remover.isPending} />
    </Drawer>
  );
}

/** Editar nome, descrição e visibilidade (dono do canal ou quem administra; discussão de registro é sempre privada). */
export function EditarCanal({ canal, aberto, aoFechar }: { canal: CanalDetalhe; aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation('equipe');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const idNome = useId();
  const idDescricao = useId();
  const idVis = useId();
  const [nome, setNome] = useState<string | null>(null);
  const [descricao, setDescricao] = useState<string | null>(null);
  const [vis, setVis] = useState<Visibilidade | null>(null);
  const valorNome = nome ?? canal.nome ?? '';
  // A descrição gravada é o valor inicial do campo (o campo é AreaSensivel: censurado sem foco no modo discreto).
  const { descricao: descricaoGravada } = canal;
  const valorDescricao = descricao ?? descricaoGravada ?? '';
  const valorVis = vis ?? canal.visibilidade;
  const fechar = () => {
    setNome(null);
    setDescricao(null);
    setVis(null);
    aoFechar();
  };
  const salvar = useMutation({
    mutationFn: () => patch(`/equipe/canais/${canal.id}`, { nome: valorNome, descricao: valorDescricao.trim() || null, ...(canal.tipo !== 'registro' ? { visibilidade: valorVis } : {}) }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['equipe'] });
      fechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      titulo={t('editar.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={fechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" disabled={!valorNome.trim()} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
            {t('acoes.salvar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Rotulo id={idNome} rotulo={t('nova.nome')} obrigatorio>
          <Entrada id={idNome} value={valorNome} maxLength={80} onChange={(e) => setNome(e.target.value)} />
        </Rotulo>
        <Rotulo id={idDescricao} rotulo={t('nova.descricao')}>
          <AreaSensivel id={idDescricao} rows={2} maxLength={500} value={valorDescricao} onChange={(e) => setDescricao(e.target.value)} className={CLASSE_AREA} />
        </Rotulo>
        {canal.tipo !== 'registro' && (
          <Rotulo id={idVis} rotulo={t('nova.visibilidade')} ajuda={valorVis === 'publico' ? t('visibilidade.publicoAjuda') : t('visibilidade.privadoAjuda')}>
            <Selecao
              id={idVis}
              opcoes={[
                { valor: 'privado', rotulo: t('visibilidade.privado') },
                { valor: 'publico', rotulo: t('visibilidade.publico') },
              ]}
              value={valorVis}
              onChange={(e) => setVis(e.target.value as Visibilidade)}
            />
          </Rotulo>
        )}
      </div>
    </Modal>
  );
}
